import { alConflictoDeHueco } from '@citas/frontal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ErrorDeApi, peticion } from './api/cliente.js';
import type { Cita, CitaCreada, DatosDeSesion, Entidad, Servicio } from './api/tipos.js';
import {
  estadoAlReiniciar,
  pasoAnterior,
  pasosVisibles,
  siguientePaso,
  type Paso,
} from './asistente.js';
import { BarraDeProgreso, ResumenDeReserva } from './componentes/comunes.js';
import { PantallaAcceso } from './pantallas/Acceso.js';
import { PantallaConfirmacion } from './pantallas/Confirmacion.js';
import { PantallaEntidad } from './pantallas/Entidad.js';
import { PantallaHora } from './pantallas/Hora.js';
import { PantallaMisCitas } from './pantallas/MisCitas.js';
import { PantallaReservada } from './pantallas/Reservada.js';
import { PantallaServicio } from './pantallas/Servicio.js';
import { conTeamSystem } from './nombres.js';
import { textos } from './textos.js';

/**
 * El portal.
 *
 * El asistente son cinco pantallas en orden (§9) y una lista de citas aparte.
 * El estado vive aquí porque es poco y se entiende de un vistazo; en cuanto la
 * fase 3 traiga el panel interno, esto pedirá un enrutador de verdad.
 *
 * La única ruta con URL propia es «Mis citas» (`#/mis-citas`), porque el §9 pide
 * que se pueda llegar desde el enlace de un correo.
 */

type Vista =
  | { tipo: 'asistente' }
  | { tipo: 'misCitas' }
  | {
      tipo: 'reservada';
      cita: CitaCreada;
      contacto: { nombre: string; telefono: string };
    };

const RUTA_MIS_CITAS = '#/mis-citas';
const URL_SOPORTE = 'https://ayuda.teamsystem.es/hc/es';
const URL_PRIVACIDAD = 'https://tc.teamsystem.com/InformativaPrivacyTSDigitalES.pdf';
const EMAIL_SOPORTE = 'clientes@sdelsol.com';
const ZONA_HORARIA_CITAS = 'Europe/Madrid';

export function App() {
  const clienteDeConsultas = useQueryClient();

  const [entidad, setEntidad] = useState<Entidad | null>(null);
  const [sesion, setSesion] = useState<DatosDeSesion | null>(null);
  const [servicio, setServicio] = useState<Servicio | null>(null);
  const [instante, setInstante] = useState<string | null>(null);
  const [paso, setPaso] = useState<Paso>('entidad');
  const zona = ZONA_HORARIA_CITAS;
  const [conflicto, setConflicto] = useState<string | null>(null);
  const [avisoMisCitas, setAvisoMisCitas] = useState<string | null>(null);
  /** Cuando se está cambiando la hora de una cita ya existente. */
  const [reprogramando, setReprogramando] = useState<Cita | null>(null);
  const [vista, setVista] = useState<Vista>(
    window.location.hash === RUTA_MIS_CITAS ? { tipo: 'misCitas' } : { tipo: 'asistente' },
  );

  // «Mis citas» tiene URL propia para poder llegar desde el correo.
  useEffect(() => {
    function alCambiarLaUrl() {
      setVista(
        window.location.hash === RUTA_MIS_CITAS ? { tipo: 'misCitas' } : { tipo: 'asistente' },
      );
    }
    window.addEventListener('hashchange', alCambiarLaUrl);
    return () => window.removeEventListener('hashchange', alCambiarLaUrl);
  }, []);

  function irA(destino: Vista) {
    const hash = destino.tipo === 'misCitas' ? RUTA_MIS_CITAS : '';
    if (window.location.hash !== hash) {
      window.history.pushState(null, '', hash === '' ? window.location.pathname : hash);
    }
    setVista(destino);
  }

  const visibles = pasosVisibles(sesion?.saltarPasoDeServicio ?? false);
  const mostrarResumen =
    vista.tipo === 'asistente' && servicio !== null && (paso === 'hora' || paso === 'confirmacion');
  const esPortada = sesion === null && vista.tipo === 'asistente' && paso === 'entidad';
  const contextoActivo =
    sesion !== null
      ? {
          etiqueta: textos.aplicacion.areaCliente,
          titulo: sesion.cliente.razonSocial,
          detalle: sesion.entidad.nombre,
        }
      : entidad !== null
        ? {
            etiqueta: textos.aplicacion.marcaSeleccionada,
            titulo: conTeamSystem(entidad.nombre),
            detalle: textos.aplicacion.identificacionSegura,
          }
        : null;

  const reprogramar = useMutation({
    mutationFn: (datos: { citaId: string; inicio: string }) =>
      peticion(`/citas/${datos.citaId}`, { metodo: 'PATCH', cuerpo: { inicio: datos.inicio } }),
    onSuccess: async () => {
      setReprogramando(null);
      setConflicto(null);
      setAvisoMisCitas(textos.misCitas.reprogramadaCorrectamente);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['citas'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['huecos'] });
      irA({ tipo: 'misCitas' });
    },
    onError: (fallo: unknown) => {
      // Que el hueco se ocupe mientras se reprograma se trata igual que al
      // reservar: se refresca la rejilla y se pide otra hora. Es la misma
      // función que usan la confirmación y el panel.
      const fueConflicto = alConflictoDeHueco(fallo, {
        olvidarHueco: () => setConflicto(textos.confirmacion.conflicto),
        refrescarHuecos: () => {
          void clienteDeConsultas.invalidateQueries({ queryKey: ['huecos'] });
        },
      });

      // Y si no lo era, se dice lo que de verdad ha pasado. Antes se enseñaba
      // el mensaje del hueco ocupado ante **cualquier** error, así que un
      // «fuera de plazo» se leía como que alguien te había quitado la hora.
      if (!fueConflicto) {
        setConflicto(fallo instanceof ErrorDeApi ? fallo.message : textos.errores.generico);
      }
    },
  });

  const cerrarSesion = useMutation({
    mutationFn: () => peticion('/auth/salir', { metodo: 'POST', cuerpo: {} }),
    onSuccess: () => {
      clienteDeConsultas.clear();
      setSesion(null);
      setEntidad(null);
      setServicio(null);
      setInstante(null);
      setAvisoMisCitas(null);
      setPaso('entidad');
      irA({ tipo: 'asistente' });
    },
  });

  /**
   * Deja el asistente listo para empezar otra reserva.
   *
   * Hay que reponer el servicio cuando el cliente solo tiene uno contratado: en
   * ese caso el paso de Servicio no existe (D-03) y nadie lo va a volver a
   * elegir, así que dejarlo a `null` y mandar al cliente al paso de la hora
   * dejaba la tarjeta **en blanco**.
   */
  function reiniciarAsistente() {
    setInstante(null);
    setConflicto(null);
    setReprogramando(null);
    setAvisoMisCitas(null);
    const siguiente = estadoAlReiniciar(sesion ?? { saltarPasoDeServicio: false, servicios: [] });
    setServicio(siguiente.servicio);
    setPaso(siguiente.paso);
    irA({ tipo: 'asistente' });
  }

  /**
   * Vuelve a la rejilla porque el hueco elegido se ocupó (§9).
   *
   * Ya no refresca aquí: de eso se encarga `alConflictoDeHueco`, que es quien
   * garantiza que olvidar el hueco y refrescar la rejilla van siempre juntos.
   */
  function volverAElegirHora(mensaje: string) {
    setConflicto(mensaje);
    setInstante(null);
    setPaso('hora');
  }

  /** El «Volver» y la flecha de cada pantalla hacen lo mismo. */
  function volverAtras() {
    if (vista.tipo === 'misCitas') {
      reiniciarAsistente();
      return;
    }
    if (paso === 'hora' && reprogramando !== null) {
      setReprogramando(null);
      irA({ tipo: 'misCitas' });
      return;
    }
    setPaso(paso === 'confirmacion' ? 'hora' : pasoAnterior(paso, visibles));
  }

  return (
    <>
      <a className="saltar" href="#contenido">
        {textos.aplicacion.saltarAlContenido}
      </a>

      <div className={`marco${esPortada ? ' marco-portada' : ' marco-operativo'}`}>
        <header className="cabecera">
          <div className="identidad">
            <img
              className="logo"
              src="/teamsystem-logo.svg"
              alt="TeamSystem"
              width="157"
              height="29"
            />
            <span className="separador-marca" aria-hidden="true" />
            <span className="nombre-producto">{textos.aplicacion.portal}</span>
          </div>
          {sesion !== null && (
            <div className="sesion">
              <span className="cliente-activo">
                <span className="indicador-activo" aria-hidden="true" />
                {sesion.entidad.nombre}
              </span>
              <nav
                aria-label="Acciones de la sesión"
                className="botonera acciones-sesion"
                style={{ marginTop: 0 }}
              >
                {vista.tipo !== 'misCitas' && (
                  <button type="button" onClick={() => irA({ tipo: 'misCitas' })}>
                    {textos.aplicacion.misCitas}
                  </button>
                )}
                <button type="button" className="enlace" onClick={() => cerrarSesion.mutate()}>
                  {textos.aplicacion.salir}
                </button>
              </nav>
            </div>
          )}
        </header>

        {esPortada ? (
          <div className="presentacion presentacion-portada">
            <div className="mensaje-portada">
              <p className="sobretitulo">{textos.aplicacion.asistencia}</p>
              <h1>{textos.aplicacion.tituloImpacto}</h1>
              <p className="subtitulo">{textos.aplicacion.subtituloImpacto}</p>
              <div className="disponibilidad-portada">
                <span aria-hidden="true" />
                {textos.aplicacion.especialistasDisponibles}
              </div>
            </div>
            <div className="visual-portada" aria-hidden="true">
              <div className="visual-detras visual-detras-dos" />
              <div className="visual-detras visual-detras-uno" />
              <div className="visual-cita">
                <div className="visual-fecha">
                  <span>{textos.aplicacion.visual.dia}</span>
                  <strong>{textos.aplicacion.visual.numero}</strong>
                  <span>{textos.aplicacion.visual.mes}</span>
                  <hr />
                  <span>{textos.aplicacion.visual.hora}</span>
                </div>
                <div className="visual-datos">
                  <em>{textos.aplicacion.portal}</em>
                  <i />
                  <span>
                    <svg viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="8" />
                      <path d="M12 8v4l2.5 1.5" />
                    </svg>
                    {textos.aplicacion.visual.duracion}
                  </span>
                  <span>{textos.aplicacion.visual.telefono}</span>
                  <span>{textos.aplicacion.visual.motivo}</span>
                  <i />
                </div>
                <span className="sello-visual">
                  <svg viewBox="0 0 24 24">
                    <path d="m6 12.5 4 4L18 8" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
        ) : (
          contextoActivo !== null && (
            <div className="contexto-cliente">
              <span className="contexto-icono" aria-hidden="true">
                <img src="/teamsystem-icon.svg" alt="" />
              </span>
              <div>
                <p>{contextoActivo.etiqueta}</p>
                <strong>{contextoActivo.titulo}</strong>
                <span>{contextoActivo.detalle}</span>
              </div>
            </div>
          )
        )}

        {esPortada && (
          <ul className="garantias" aria-label={textos.aplicacion.garantiasAria}>
            <li>
              <span className="icono-garantia" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M7 10V8a5 5 0 0 1 10 0v2m-11 0h12a2 2 0 0 1 2 2v7H4v-7a2 2 0 0 1 2-2Zm6 3v3" />
                </svg>
              </span>
              <span>
                <strong>{textos.aplicacion.accesoSeguro}</strong>
                <small>{textos.aplicacion.accesoSeguroDetalle}</small>
              </span>
            </li>
            <li>
              <span className="icono-garantia" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
              </span>
              <span>
                <strong>{textos.aplicacion.horarioLocal}</strong>
                <small>{textos.aplicacion.horarioLocalDetalle}</small>
              </span>
            </li>
            <li>
              <span className="icono-garantia" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M3 6.5 12 13l9-6.5M5 5h14a2 2 0 0 1 2 2v11H3V7a2 2 0 0 1 2-2Z" />
                  <path d="m16 15 1.5 1.5L21 13" />
                </svg>
              </span>
              <span>
                <strong>{textos.aplicacion.confirmacionCorreo}</strong>
                <small>{textos.aplicacion.confirmacionCorreoDetalle}</small>
              </span>
            </li>
          </ul>
        )}

        <main
          id="contenido"
          className={`tarjeta${mostrarResumen ? ' tarjeta-con-resumen' : ''}${
            esPortada ? ' tarjeta-portada' : ' tarjeta-operativa'
          }${vista.tipo === 'misCitas' ? ' tarjeta-mis-citas' : ''}${
            vista.tipo === 'reservada' ? ' tarjeta-reservada' : ''
          }`}
        >
          {vista.tipo === 'misCitas' && sesion !== null && (
            <PantallaMisCitas
              zona={zona}
              aviso={avisoMisCitas}
              alDescartarAviso={() => setAvisoMisCitas(null)}
              alReprogramar={(cita) => {
                const suServicio = sesion.servicios.find((s) => s.id === cita.servicio.id);
                if (suServicio === undefined) return;
                setReprogramando(cita);
                setServicio(suServicio);
                setConflicto(null);
                setAvisoMisCitas(null);
                setPaso('hora');
                irA({ tipo: 'asistente' });
              }}
              alVolver={reiniciarAsistente}
            />
          )}

          {vista.tipo === 'misCitas' && sesion === null && (
            <PantallaEntidad
              alElegir={(elegida) => {
                setEntidad(elegida);
                setPaso('acceso');
                irA({ tipo: 'asistente' });
              }}
            />
          )}

          {vista.tipo === 'reservada' && (
            <PantallaReservada
              cita={vista.cita}
              zona={zona}
              contactoNombre={vista.contacto.nombre}
              contactoTelefono={vista.contacto.telefono}
              alVerMisCitas={() => irA({ tipo: 'misCitas' })}
              alReservarOtra={reiniciarAsistente}
            />
          )}

          {vista.tipo === 'asistente' && (
            <>
              {sesion !== null && reprogramando === null && (
                <BarraDeProgreso visibles={visibles} actual={paso} />
              )}

              {paso === 'entidad' && (
                <PantallaEntidad
                  alElegir={(elegida) => {
                    setEntidad(elegida);
                    setPaso('acceso');
                  }}
                />
              )}

              {paso === 'acceso' && entidad !== null && (
                <PantallaAcceso
                  entidad={entidad}
                  alEntrar={(datos) => {
                    setSesion(datos);
                    const siguientes = pasosVisibles(datos.saltarPasoDeServicio);
                    if (datos.saltarPasoDeServicio) setServicio(datos.servicios[0] ?? null);
                    setPaso(siguientePaso('acceso', siguientes));
                  }}
                  alVolver={volverAtras}
                />
              )}

              {paso === 'servicio' && sesion !== null && (
                <PantallaServicio
                  servicios={sesion.servicios}
                  alElegir={(elegido) => {
                    setServicio(elegido);
                    setPaso('hora');
                  }}
                  alVolver={volverAtras}
                />
              )}

              {paso === 'hora' && servicio !== null && (
                <PantallaHora
                  servicio={servicio}
                  zona={zona}
                  avisoDeConflicto={conflicto}
                  alElegir={(elegido) => {
                    setConflicto(null);
                    if (reprogramando !== null) {
                      reprogramar.mutate({ citaId: reprogramando.id, inicio: elegido });
                      return;
                    }
                    setInstante(elegido);
                    setPaso('confirmacion');
                  }}
                  alVolver={volverAtras}
                />
              )}

              {paso === 'confirmacion' && servicio !== null && instante !== null && (
                <PantallaConfirmacion
                  servicio={servicio}
                  instanteIso={instante}
                  zona={zona}
                  alReservar={(cita, contacto) => {
                    void clienteDeConsultas.invalidateQueries({ queryKey: ['citas'] });
                    void clienteDeConsultas.invalidateQueries({ queryKey: ['huecos'] });
                    irA({ tipo: 'reservada', cita, contacto });
                  }}
                  alVolver={volverAtras}
                  alConflicto={volverAElegirHora}
                />
              )}
            </>
          )}

          {mostrarResumen && servicio !== null && (
            <ResumenDeReserva
              entidad={entidad?.nombre ?? sesion?.entidad.nombre ?? ''}
              servicio={servicio.nombre}
              duracionMin={servicio.duracionMin}
              instanteIso={instante}
              zona={zona}
            />
          )}
        </main>

        <footer className="pie">
          <p>{textos.aplicacion.pie}</p>
          <nav aria-label="Ayuda y condiciones">
            <a href={URL_SOPORTE} target="_blank" rel="noreferrer">
              {textos.aplicacion.soporte}
            </a>
            <a href={`mailto:${EMAIL_SOPORTE}`}>{EMAIL_SOPORTE}</a>
            <a href={URL_PRIVACIDAD} target="_blank" rel="noreferrer">
              {textos.aplicacion.privacidad}
            </a>
          </nav>
        </footer>
      </div>
    </>
  );
}
